import { useMutation } from '@apollo/client';
import type { SafetyAction, UpsertActionInput } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_GROUP_ACTION, GET_COMMUNITY_GROUP_ACTIONS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface UpsertCommunityGroupActionResponse {
  upsertCommunityGroupAction: SafetyAction;
}

export function useUpsertCommunityGroupAction(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [upsertActionMutation, { loading }] = useMutation<UpsertCommunityGroupActionResponse>(UPSERT_COMMUNITY_GROUP_ACTION, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP_ACTIONS, variables: { communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertAction = async (communityGroupId: string, actionName: string, input: UpsertActionInput): Promise<boolean> => {
    try {
      track('UpsertCommunityGroupActionInitiated', {
        communityGroupId,
        actionName,
      });

      await upsertActionMutation({
        variables: {
          communityGroupId,
          actionName,
          input,
        },
      });

      track('UpsertCommunityGroupActionSucceeded', {
        communityGroupId,
        actionName,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityGroupActionFailed', {
        communityGroupId,
        actionName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertAction,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityGroupAction;
